import { useCallback, useEffect, useRef, useState } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import type {
    PatchRequest,
    SubmissionDTO,
    SubmitClientDump,
    SubmitMainDump,
    SubmitRequest,
    VisitCardDTO,
    VisitCardAPI,
} from "@/api/visit_card_client"
import { createVisitCardApi } from "@/api/visit_card_client"
import { api } from "@/api/instance"
import { qk } from "@/shared/api/queryKeys"

export type ReferencesMap = Record<string, Array<{ id: string | number; name: string; [k: string]: unknown }>>

/** How long field flash highlights stay visible after a live / demo update. */
export const VISIT_CARD_FLASH_MS = 1500

export interface VisitCardUpdatePayload {
    type: "visit_card_update"
    session_id: string
    fields: {
        client?: Record<string, string | null>
        dropdowns?: Record<string, {
            id: string | number | null
            name: string | null
            raw: string | null
            confidence: number
            source: string
        }>
        comments?: string
        submission?: SubmissionDTO
    }
}

export type VisitCardUpdateFields = VisitCardUpdatePayload["fields"]

/**
 * Pure merge of a visit_card_update `fields` block into a card snapshot.
 * Skips keys listed in the optional manual-edit sets (live designer edits).
 */
export function mergeVisitCardUpdate(
    card: VisitCardDTO,
    fields: VisitCardUpdateFields,
    opts?: {
        skipClientKeys?: ReadonlySet<string>
        skipDropdownKeys?: ReadonlySet<string>
    },
): VisitCardDTO {
    const next = structuredClone(card) as VisitCardDTO
    const skipClient = opts?.skipClientKeys
    const skipDropdown = opts?.skipDropdownKeys

    if (fields.client) {
        for (const [key, value] of Object.entries(fields.client)) {
            if (skipClient?.has(key)) continue
            if (key in next.client) {
                ;(next.client as unknown as Record<string, unknown>)[key] = value
            }
        }
    }

    if (fields.dropdowns) {
        for (const [flatKey, value] of Object.entries(fields.dropdowns)) {
            if (skipDropdown?.has(flatKey)) continue
            applyDropdownUpdate(next, flatKey, value)
        }
    }

    if (typeof fields.comments === "string") {
        next.comments = fields.comments
    }

    return next
}

/**
 * Карточка обращения: state + операции + merge live-обновлений из SSE.
 *
 * `card` stays in local React state (not Query cache) because SSE merges
 * must respect designer manual edits via manualClientRef/manualDropdownRef.
 * See docs/architecture/query-exceptions.md.
 *
 * Возвращает `onVisitCardUpdate` — колбэк, который надо передать в
 * `useCopilot`, чтобы фронт получал live-апдейты из общего SSE-стрима.
 */
export function useVisitCard(enabled: boolean) {
    const apiRef = useRef<VisitCardAPI<unknown> | null>(null)
    if (apiRef.current === null) {
        apiRef.current = createVisitCardApi(api)
    }

    const [card, setCard] = useState<VisitCardDTO | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [flashes, setFlashes] = useState<string[]>([])
    const flashTimerRef = useRef<number | null>(null)
    const manualClientRef = useRef<Set<string>>(new Set())
    const manualDropdownRef = useRef<Set<string>>(new Set())
    const sessionIdRef = useRef<string | null>(null)

    const referencesQuery = useQuery({
        queryKey: qk.visitCard.references(),
        queryFn: async () => {
            const response = await apiRef.current!.visitCard.referencesAllVisitCardReferencesGet()
            return (response.data as ReferencesMap) ?? {}
        },
        enabled,
        staleTime: 60_000,
    })

    const dealerId = card?.dealer?.id ?? null
    const designersQuery = useQuery({
        queryKey: qk.visitCard.designers(String(dealerId ?? "")),
        queryFn: async () => {
            const response = await apiRef.current!.visitCard.referencesByKeyVisitCardReferencesKeyGet(
                `employees-designers?parent_diler=${dealerId}`,
            )
            const items = response.data as unknown as ReferencesMap[string]
            return Array.isArray(items) ? items : []
        },
        enabled: enabled && dealerId != null,
        staleTime: 60_000,
    })

    const references: ReferencesMap = {
        ...(referencesQuery.data ?? {}),
        ...(designersQuery.data
            ? { "employees-designers": designersQuery.data }
            : {}),
    }

    const referencesLoaded = referencesQuery.isSuccess || referencesQuery.isError
    const referencesError = (() => {
        if (referencesQuery.isError) {
            return `Не удалось загрузить справочники: ${(referencesQuery.error as Error).message}. Списки временно пусты.`
        }
        if (!referencesQuery.data) return null
        const nonEmpty = Object.values(referencesQuery.data).some(
            (items) => Array.isArray(items) && items.length > 0,
        )
        return nonEmpty
            ? null
            : "Справочники недоступны (нет связи со справочной системой). Поля можно заполнить вручную текстом; выпадающие списки временно пусты."
    })()

    const { mutateAsync: initAsync } = useMutation({
        mutationFn: async ({
            sessionId,
            phone,
            profileName,
        }: {
            sessionId: string
            phone?: string
            profileName?: string
        }) => {
            const response = await apiRef.current!.copilot.initCardCopilotSessionSessionIdVisitCardInitPost(
                sessionId,
                { phone: phone ?? null, profile_name: profileName ?? null },
            )
            return response.data.card
        },
        onSuccess: (nextCard) => {
            setCard(nextCard)
        },
        onError: (e) => {
            setError(`init failed: ${(e as Error).message}`)
        },
    })

    const { mutateAsync: patchAsync } = useMutation({
        mutationFn: async ({
            sessionId,
            patch,
        }: {
            sessionId: string
            patch: PatchRequest
        }) => {
            const response = await apiRef.current!.copilot.patchCardCopilotSessionSessionIdVisitCardPatch(
                sessionId,
                patch,
            )
            return { card: response.data.card, patch }
        },
        onSuccess: ({ card: nextCard, patch }) => {
            setCard(nextCard)
            if (patch.client) {
                for (const key of Object.keys(patch.client)) {
                    manualClientRef.current.add(key)
                }
            }
            if (patch.dropdowns) {
                for (const key of Object.keys(patch.dropdowns)) {
                    manualDropdownRef.current.add(key)
                }
            }
        },
        onError: (e) => {
            setError(`Не удалось сохранить изменение: ${(e as Error).message}`)
        },
    })

    const { mutateAsync: submitAsync, isPending: isSubmitting } = useMutation({
        mutationFn: async ({
            sessionId,
            body,
        }: {
            sessionId: string
            body: SubmitRequest
        }) => {
            const response = await apiRef.current!.copilot.submitCardCopilotSessionSessionIdVisitCardSubmitPost(
                sessionId,
                body,
            )
            return response.data
        },
        onSuccess: (data) => {
            setCard(data.card)
        },
        onError: (e) => {
            setError(`Не удалось отправить карточку: ${(e as Error).message}`)
        },
    })

    const init = useCallback(
        async (sessionId: string, phone?: string, profileName?: string) => {
            if (!enabled || !apiRef.current) return
            if (sessionIdRef.current !== sessionId) {
                sessionIdRef.current = sessionId
                manualClientRef.current.clear()
                manualDropdownRef.current.clear()
                if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current)
                flashTimerRef.current = null
                setFlashes([])
            }
            try {
                await initAsync({ sessionId, phone, profileName })
            } catch {
                // Inline error already set in mutation onError.
            }
        },
        [enabled, initAsync],
    )

    const refresh = useCallback(async (sessionId: string) => {
        if (!enabled || !apiRef.current) return
        try {
            const response = await apiRef.current.copilot.getCardCopilotSessionSessionIdVisitCardGet(sessionId)
            setCard(response.data.card)
        } catch {
            /* 404 если карточки нет — норма пока сессия не открыта */
        }
    }, [enabled])

    const patchCard = useCallback(async (sessionId: string, patch: PatchRequest) => {
        if (!enabled || !apiRef.current) return
        try {
            await patchAsync({ sessionId, patch })
        } catch {
            // Inline error already set in mutation onError.
        }
    }, [enabled, patchAsync])

    const submit = useCallback(async (
        sessionId: string,
        payload: { profile_name: string; client: SubmitClientDump; main: SubmitMainDump },
    ) => {
        if (!enabled || !apiRef.current) return null
        try {
            const data = await submitAsync({
                sessionId,
                body: {
                    commit: "T",
                    profile_name: payload.profile_name,
                    client: payload.client,
                    main: payload.main,
                },
            })
            return data.submission
        } catch {
            return null
        }
    }, [enabled, submitAsync])

    /**
     * Колбэк для useCopilot: merge live-обновления в локальный state и
     * подсветка изменённых полей (flashes с автосбросом).
     */
    const onVisitCardUpdate = useCallback((payload: VisitCardUpdatePayload) => {
        const fields = payload.fields ?? {}
        const flashed = Object.keys(fields.dropdowns ?? {}).filter(
            (flatKey) => !manualDropdownRef.current.has(flatKey),
        )
        setCard((prev) => {
            if (prev === null) return prev
            return mergeVisitCardUpdate(prev, fields, {
                skipClientKeys: manualClientRef.current,
                skipDropdownKeys: manualDropdownRef.current,
            })
        })
        if (flashed.length > 0) {
            setFlashes((prev) => Array.from(new Set([...prev, ...flashed])))
            if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current)
            flashTimerRef.current = window.setTimeout(() => {
                flashTimerRef.current = null
                setFlashes([])
            }, VISIT_CARD_FLASH_MS)
        }
    }, [])

    useEffect(() => {
        return () => {
            if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current)
        }
    }, [])

    /** Drop local card state when leaving a meeting / switching clients. */
    const reset = useCallback(() => {
        sessionIdRef.current = null
        manualClientRef.current.clear()
        manualDropdownRef.current.clear()
        if (flashTimerRef.current !== null) {
            window.clearTimeout(flashTimerRef.current)
            flashTimerRef.current = null
        }
        setFlashes([])
        setError(null)
        setCard(null)
    }, [])

    return {
        card,
        references,
        referencesLoaded,
        referencesError,
        retryReferences: () => {
            void referencesQuery.refetch()
        },
        isSubmitting,
        error,
        init,
        reset,
        refresh,
        patchCard,
        submit,
        onVisitCardUpdate,
        flashes,
        clearError: () => setError(null),
    }
}


export type VisitCardDropdownUpdate =
    NonNullable<VisitCardUpdatePayload["fields"]["dropdowns"]>[string]

/** Apply one dropdown flatKey onto a VisitCardDTO (mutates `card`). */
export function applyDropdownUpdate(
    card: VisitCardDTO,
    flatKey: string,
    value: VisitCardDropdownUpdate,
): void {
    const parts = flatKey.split("__")
    // в карточке dropdown'ы хранятся как FieldValue по path:
    //   "dealer"                  → card.dealer
    //   "client__client_type"     → card.client.client_type
    //   "funnel__room"            → card.funnel.room
    let target: Record<string, unknown> | null | undefined =
        card as unknown as Record<string, unknown>
    for (let i = 0; i < parts.length - 1; i++) {
        target = target?.[parts[i]] as Record<string, unknown> | null | undefined
        if (target == null) return
    }
    const leaf = parts[parts.length - 1]
    if (!target || typeof target !== "object" || !(leaf in target)) return
    // value === null означает «обнулить поле» (юзер нажал X в комбобоксе,
    // или backend прислал очистку). Без этой ветки `value.id` крашит весь
    // UI с TypeError: Cannot read properties of null (reading 'id').
    if (value == null) {
        target[leaf] = {
            id: null,
            name: null,
            raw: null,
            confidence: 0,
            source: "unset" as never,
            manual: false,
        }
        return
    }
    target[leaf] = {
        id: value.id ?? null,
        name: value.name ?? null,
        raw: value.raw ?? null,
        confidence: value.confidence ?? 0,
        source: (value.source ?? "unset") as never,
        manual: false,
    }
}
